import { Injectable } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel
} from '@microsoft/signalr';

import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class NotificationSignalRService {

    private hubConnection: HubConnection | null = null;

    private notificationSource = new Subject<any>();

    public notification$ =
        this.notificationSource.asObservable();

    private isStarting = false;

    private isStopping = false;

    private isInBackForwardCache = false;


    constructor(
        private http: HttpClient
    ) {

        console.log('======================================');
        console.log('NotificationSignalRService CREATED');
        console.log('======================================');

        window.addEventListener(
            'pagehide',
            this.onPageHide
        );

        window.addEventListener(
            'pageshow',
            this.onPageShow
        );
    }


    /**
     * =========================================================
     * START SQL NOTIFICATION LISTENER
     * =========================================================
     *
     * Calls backend API once.
     *
     * This initializes SqlDependency and registers
     * OnDependencyChange event in the backend.
     */
    private startNotificationListener(): void {

        console.log('======================================');
        console.log('Starting SQL Notification listener...');
        console.log('======================================');

        this.http
            .get(
                environment.notificationApiUrl,
                {
                    responseType: 'text'
                }
            )
            .subscribe(
                (data) => {

                    console.log(
                        'SQL Notification listener started successfully.'
                    );

                    console.log(
                        'Response:',
                        data
                    );
                },
                (error) => {

                    console.error(
                        'ERROR starting SQL Notification listener:',
                        error
                    );
                }
            );
    }


    /**
     * =========================================================
     * PAGE HIDE
     * =========================================================
     */
    private onPageHide =
        (event: PageTransitionEvent): void => {

            console.log('======================================');
            console.log('SignalR: pagehide event');
            console.log(
                'BFCache persisted:',
                event.persisted
            );
            console.log(
                'Current URL:',
                window.location.href
            );
            console.log('======================================');

            if (event.persisted) {

                console.log(
                    'SignalR: Page is entering Back-Forward Cache'
                );

                this.isInBackForwardCache = true;

                this.stopForBackForwardCache();
            }
        };


    /**
     * =========================================================
     * STOP CONNECTION FOR BFCache
     * =========================================================
     */
    private stopForBackForwardCache(): void {

        console.log(
            'SignalR: stopForBackForwardCache() called'
        );

        this.isStarting = false;

        if (!this.hubConnection) {

            console.log(
                'SignalR: No connection exists while entering BFCache.'
            );

            return;
        }

        const connection =
            this.hubConnection;

        this.hubConnection = null;

        connection
            .stop()
            .then(() => {

                console.log(
                    'SignalR connection stopped for BFCache.'
                );
            })
            .catch((error) => {

                console.warn(
                    'Error stopping SignalR for BFCache:',
                    error
                );
            });
    }


    /**
     * =========================================================
     * PAGE SHOW
     * =========================================================
     */
    private onPageShow =
        (event: PageTransitionEvent): void => {

            console.log('======================================');
            console.log('SignalR: pageshow event');
            console.log(
                'BFCache persisted:',
                event.persisted
            );
            console.log(
                'Current URL:',
                window.location.href
            );
            console.log('======================================');

            if (event.persisted) {

                console.log(
                    'SignalR: Page restored from Back-Forward Cache'
                );

                this.isInBackForwardCache = false;

                setTimeout(() => {

                    this.ensureConnection();

                }, 100);
            }
        };


    /**
     * =========================================================
     * START SIGNALR CONNECTION
     * =========================================================
     */
    public startConnection(): void {

        console.log('--------------------------------------');
        console.log('>>> startConnection() CALLED');
        console.log('--------------------------------------');

        if (this.isInBackForwardCache) {

            console.log(
                'SignalR start skipped because page is in BFCache.'
            );

            return;
        }

        if (this.isStopping) {

            console.log(
                'SignalR start skipped because connection was intentionally stopped.'
            );

            return;
        }

        if (this.isStarting) {

            console.log(
                'SignalR connection is already starting.'
            );

            return;
        }

        if (
            this.hubConnection &&
            (
                this.hubConnection.state ===
                    HubConnectionState.Connected ||

                this.hubConnection.state ===
                    HubConnectionState.Connecting
            )
        ) {

            console.log(
                'SignalR connection already exists.'
            );

            console.log(
                'Current State:',
                this.hubConnection.state
            );

            return;
        }

        this.isStopping = false;

        this.isStarting = true;

        console.log(
            'Creating SignalR connection...'
        );

        this.hubConnection =
            new HubConnectionBuilder()

                .withUrl(
                    environment.signalRHubUrl
                )

                .configureLogging(
                    LogLevel.Information
                )

                .build();


        console.log(
            'SignalR HubConnection object created'
        );


        /**
         * =====================================================
         * BROADCAST NOTIFICATION
         * =====================================================
         */
        this.hubConnection.on(
            'BroadcastNotification',
            (message: any) => {                

                this.notificationSource.next(
                    message
                );
            }
        );


        console.log(
            'Registered SignalR event: BroadcastNotification'
        );


        /**
         * =====================================================
         * CONNECTION CLOSED
         * =====================================================
         */
        this.hubConnection.onclose(
            (error) => {                

                this.isStarting = false;

                if (this.isStopping) {

                    console.log(
                        'SignalR was intentionally stopped.'
                    );

                    return;
                }

                if (this.isInBackForwardCache) {

                    console.log(
                        'SignalR closed because page entered BFCache.'
                    );

                    return;
                }

                console.warn(
                    'SignalR connection closed unexpectedly.'
                );

                console.warn(
                    'Automatic reconnect is disabled.'
                );
            }
        );


        /**
         * =====================================================
         * START CONNECTION
         * =====================================================
         */
        console.log(
            'Calling hubConnection.start()...'
        );

        const connection =
            this.hubConnection;

        connection
            .start()

            .then(() => {

                this.isStarting = false;

                if (this.isInBackForwardCache) {

                    console.log(
                        'Page entered BFCache while SignalR was starting.'
                    );

                    const currentConnection =
                        this.hubConnection;

                    this.hubConnection = null;

                    if (currentConnection) {

                        currentConnection
                            .stop()
                            .catch((error) => {

                                console.warn(
                                    'Error stopping SignalR after BFCache:',
                                    error
                                );
                            });
                    }

                    return;
                }

                console.log(
                    '======================================'
                );

                console.log(
                    'SIGNALR CONNECTION STARTED SUCCESSFULLY'
                );

                console.log(
                    'Connection State:',
                    this.hubConnection
                        ? this.hubConnection.state
                        : 'null'
                );

                console.log(
                    '======================================'
                );


                /**
                 * =============================================
                 * START SQL DEPENDENCY LISTENER
                 * =============================================
                 *
                 * IMPORTANT:
                 * Call Notification API after SignalR connection
                 * is successfully established.
                 */
                this.startNotificationListener();
            })

            .catch((error) => {

                this.isStarting = false;

                console.error(
                    '======================================'
                );

                console.error(
                    'ERROR WHILE ESTABLISHING SIGNALR CONNECTION'
                );

                console.error(
                    'Error:',
                    error
                );

                console.error(
                    '======================================'
                );
            });
    }


    /**
     * =========================================================
     * ENSURE CONNECTION
     * =========================================================
     */
    public ensureConnection(): void {

        console.log(
            'SignalR ensureConnection() called'
        );

        if (this.isStopping) {

            console.log(
                'SignalR is intentionally stopped.'
            );

            return;
        }

        if (this.isInBackForwardCache) {

            console.log(
                'Page is still in BFCache.'
            );

            return;
        }

        if (this.isStarting) {

            console.log(
                'SignalR connection is already starting.'
            );

            return;
        }

        const refreshToken =
            sessionStorage.getItem(
                'refreshToken'
            );

        if (!refreshToken) {

            console.log(
                'No refresh token. SignalR will not start.'
            );

            return;
        }

        if (!this.hubConnection) {

            console.log(
                'No SignalR connection exists.'
            );

            console.log(
                'User is logged in. Starting SignalR.'
            );

            this.startConnection();

            return;
        }

        if (
            this.hubConnection.state ===
            HubConnectionState.Disconnected
        ) {

            console.log(
                'SignalR is disconnected.'
            );

            console.log(
                'User is logged in. Restarting SignalR.'
            );

            this.hubConnection = null;

            this.startConnection();

            return;
        }

        console.log(
            'SignalR connection is already active.'
        );

        console.log(
            'Current State:',
            this.hubConnection.state
        );
    }


    /**
     * =========================================================
     * STOP SIGNALR
     * =========================================================
     */
    public stopConnection(): void {

        console.log(
            '>>> stopConnection() CALLED'
        );

        this.isStopping = true;

        this.isStarting = false;

        this.isInBackForwardCache = false;

        if (!this.hubConnection) {

            console.log(
                'No SignalR connection exists.'
            );

            return;
        }

        const connection =
            this.hubConnection;

        this.hubConnection = null;

        connection
            .stop()
            .then(() => {

                console.log(
                    'SignalR connection stopped successfully.'
                );
            })
            .catch((error) => {

                console.error(
                    'Error stopping SignalR connection:',
                    error
                );
            });
    }


    /**
     * =========================================================
     * DESTROY SERVICE
     * =========================================================
     */
    public destroy(): void {

        console.log(
            'Destroying NotificationSignalRService'
        );

        this.stopConnection();

        window.removeEventListener(
            'pagehide',
            this.onPageHide
        );

        window.removeEventListener(
            'pageshow',
            this.onPageShow
        );
    }
}